import { useEffect, useId, useRef } from 'react';

import { useBreakPoints, useFormListValidator } from 'core';
import { Alert, Collapse, CurrencyButtonGroup, CurrencyInput, Stack, Text } from 'ui';
import { useForm } from 'ui/Form';
import { useDebouncedValue } from 'ui/Hooks';

type DonationAmountFormType = {
  value?: number | string;
  label?: string;
  maxAmount?: number | '';
};

const donateFormSettings = {
  donation: {
    valueError: 'Donation amount must be more than $5.00',
    validate: {
      value(value) {
        if (!value) return true;
        if (value === '') return true;
        if (Number(value) < 500) return true;
      },
    },
  },
  pledge: {
    valueError: 'Donation amount must be more than $1.00',
    validate: {
      value(value) {
        if (!value) return true;
        if (value === '') return true;
        if (Number(value) < 100) return true;
      },
      maxAmount: (value, values) => {
        if (!value) return undefined;
        if (Number(value) < 100) return 'Maximum amount must be greater than $1.00';
        if (values.value) {
          if (Number(value) % Number(values.value) !== 0)
            return 'Maximum amount must be in multiples of pledge amount';
        }
        return undefined;
      },
    },
  },
} as const;

const useDonateForm = (isCartPledge = false) => {
  const formSettings = isCartPledge ? donateFormSettings.pledge : donateFormSettings.donation;
  const form = useForm<DonationAmountFormType>({
    initialValues: {
      value: '',
      label: '',
      maxAmount: '',
    },
    validate: formSettings.validate,
    validateInputOnBlur: true,
    validateInputOnChange: false,
  });
  const [debouncedAmount] = useDebouncedValue(form.values.value, 500, { leading: false });
  const [debouncedMaxAmount] = useDebouncedValue(form.values.maxAmount, 500, { leading: false });

  const isError = form.errors.value ? formSettings.valueError : false;

  // Because max-amount validation relies on value, we need to trigger validation on value change
  useEffect(() => {
    if (form.values.value) {
      form.validateField('maxAmount');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only update on the value change
  }, [form.values.value]);

  return { form, debouncedAmount, isError, debouncedMaxAmount };
};

type DonationAmountGroupProps = {
  onChange: ({
    value,
    label,
    maxAmount,
  }: {
    value: number;
    label?: string;
    maxAmount?: number;
  }) => void;

  isPledge: boolean;
  pricing:
    | {
        value: number;
        label?: string | undefined;
      }[]
    | undefined;
};

const DonationAmountGroup = ({ onChange, isPledge, pricing }: DonationAmountGroupProps) => {
  const maxAmountRef = useRef<HTMLInputElement>(null);
  const formUuid = useId();

  const { isSmallScreen } = useBreakPoints();

  const { form, isError } = useDonateForm(isPledge);

  const handleChange = ({ value, label }: { value?: number | string; label?: string }) => {
    form.setFieldValue('value', value);
    label && form.setFieldValue('label', label);
  };

  useEffect(() => {
    // Initialize the form when it loads, this ensures that the formValidator is set with the form
    form.initialize({
      value: '',
    });
    if (form.isValid() && typeof form.values.value === 'number') {
      const updateCartObj = {
        value: form.values.value,
        // If the label is the same as the formUuid (customKey), we don't want to send it as a designation for the cart item
        label: form.values.label === formUuid ? undefined : form.values.label,
      };
      if (form.values.maxAmount) {
        Object.assign(updateCartObj, { maxAmount: form.values.maxAmount });
      }
      onChange(updateCartObj);
    }
    if (!form.values.maxAmount && isPledge && !form.values.label) {
      maxAmountRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this is intentional as we only the useEffect to run when the debouncedAmount changes, not the form values although we are using those for the final onChange
  }, [form.values]);

  useFormListValidator({ form, id: 'donation_amount' });

  return (
    <Stack gap="lg">
      <CurrencyButtonGroup
        data={pricing}
        span={isSmallScreen ? 4 : 3}
        {...form.getInputProps('value')}
        value={{ value: form.values.value, label: form.values.label }}
        onChange={handleChange}
        customKey={formUuid}
      />
      <Collapse in={Boolean(isError)}>
        <Alert title={isError} type="error" />
      </Collapse>
      <Collapse in={isPledge}>
        <Stack>
          <Text fz={{ base: 'sm', sm: 'md' }} fw={600} c="gray.8">
            {`Set Maximum Donation Amount (optional)`}
          </Text>
          <CurrencyInput
            label=""
            ref={maxAmountRef}
            placeholder={`$ Enter max donation amount`}
            {...form.getInputProps('maxAmount')}
          />
        </Stack>
      </Collapse>
    </Stack>
  );
};

export { DonationAmountGroup };
